import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, doc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import FicheClient from './FicheClient';
import { db } from '../firebase/config';
import { useLang, LangToggle } from '../LanguageContext';
import { t } from '../i18n';

const COULEUR = '#1C2833';

export default function DispatchClients({ onClose }) {
  const [clients, setClients] = useState([]);
  const [commerciaux, setCommerciaux] = useState([]);
  const [loading, setLoading] = useState(true);
  const { lang, isRTL } = useLang();
  const [selected, setSelected] = useState([]);
  const [filtreType, setFiltreType] = useState('Tous');
  const [filtreWilaya, setFiltreWilaya] = useState('Toutes');
  const [filtreComm, setFiltreComm] = useState('Tous');
  const [recherche, setRecherche] = useState('');
  const [showAssign, setShowAssign] = useState(false);
  const [commCible, setCommCible] = useState('');
  const [saving, setSaving] = useState(false);
  const [showAddClient, setShowAddClient] = useState(false);
  const [clientACorreger, setClientACorreger] = useState(null);
  const [gpsCorrection, setGpsCorrection] = useState('');
  const [ficheClient, setFicheClient] = useState(null);
  const [nouveauClient, setNouveauClient] = useState({
    nom: '', telephone: '', adresse: '', commune: '',
    wilaya: 'Constantine', type: 'Prospect', potentiel: 'Moyen', gpsText: ''
  });

  useEffect(() => { charger(); }, []);

  const charger = async () => {
    setLoading(true);
    try {
      const cSnap = await getDocs(collection(db, 'clients'));
      setClients(cSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      const uSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'commercial')));
      setCommerciaux(uSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) { console.log(e); }
    setLoading(false);
  };

  const toggleSelect = (id) => {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  };

  const selectAll = () => {
    const ids = clientsFiltres.map(c => c.id);
    setSelected(s => s.length === ids.length ? [] : ids);
  };

  const assigner = async () => {
    if (!commCible || selected.length === 0) return;
    setSaving(true);
    try {
      for (const id of selected) {
        const client = clients.find(c => c.id === id);
        const ids = client.commercialIds || [];
        if (!ids.includes(commCible)) {
          await updateDoc(doc(db, 'clients', id), { commercialIds: [...ids, commCible] });
        }
      }
      await charger();
      setSelected([]);
      setShowAssign(false);
      setCommCible('');
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const desassigner = async (clientId, commId) => {
    try {
      const client = clients.find(c => c.id === clientId);
      const ids = (client.commercialIds || []).filter(x => x !== commId);
      await updateDoc(doc(db, 'clients', clientId), { commercialIds: ids });
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
  };

  const parseGps = (text) => {
    const parts = text.replace(/\s/g, '').split(',');
    if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parseFloat(parts[0]), lng: parseFloat(parts[1]) };
    }
    return null;
  };

  const ajouterClient = async () => {
    if (!nouveauClient.nom.trim()) return;
    setSaving(true);
    try {
      const gps = nouveauClient.gpsText ? parseGps(nouveauClient.gpsText) : null;
      const { gpsText, ...clientData } = nouveauClient;
      await addDoc(collection(db, 'clients'), { ...clientData, gps, commercialIds: [], createdAt: serverTimestamp() });
      setShowAddClient(false);
      setNouveauClient({ nom: '', telephone: '', adresse: '', commune: '', wilaya: 'Constantine', type: 'Prospect', potentiel: 'Moyen', gpsText: '' });
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const enregistrerGps = async () => {
    if (!clientACorreger || !gpsCorrection.trim()) return;
    const gps = parseGps(gpsCorrection);
    if (!gps) { alert('Format invalide. Exemple : 36.3650, 6.6147'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'clients', clientACorreger.id), { gps });
      setClientACorreger(null);
      setGpsCorrection('');
      await charger();
    } catch (e) { alert('Erreur: ' + e.message); }
    setSaving(false);
  };

  const wilayas = ['Toutes', ...new Set(clients.map(c => c.wilaya || c.commune || 'Constantine'))];
  const types = ['Tous', 'Prospect', 'Client actif', 'Client inactif', 'Nouveau client'];

  const clientsFiltres = clients.filter(c => {
    const matchType = filtreType === 'Tous' || c.type === filtreType || c.statut === filtreType;
    const matchWilaya = filtreWilaya === 'Toutes' || c.wilaya === filtreWilaya || c.commune === filtreWilaya;
    const matchComm = filtreComm === 'Tous' || (filtreComm === 'Non assigné' ? (!c.commercialIds || c.commercialIds.length === 0) : c.commercialIds?.includes(filtreComm));
    const matchRecherche = !recherche || c.nom?.toLowerCase().includes(recherche.toLowerCase());
    return matchType && matchWilaya && matchComm && matchRecherche;
  });

  const getNomComm = (id) => {
    const c = commerciaux.find(x => x.id === id);
    return c ? `${c.prenom} ${c.nom}` : id;
  };

  if (ficheClient) return <FicheClient client={ficheClient} user={{uid: 'manager'}} isManager={true} onClose={() => { setFicheClient(null); charger(); }} />;

  return (
    <div style={{ ...styles.app, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={styles.topbar}>
        <button onClick={onClose} style={styles.backBtn}>←</button>
        <div style={{ flex: 1 }}>
          <div style={styles.topbarTitle}>Dispatch Clients</div>
          <div style={styles.topbarSub}>{clients.length} clients · {selected.length} sélectionnés</div>
        </div>
        <button style={styles.addBtn} onClick={() => setShowAddClient(true)}>+ Client</button>
      </div>

      <div style={styles.searchWrap}>
        <input style={styles.searchInput} placeholder="🔍 Rechercher..." value={recherche} onChange={e => setRecherche(e.target.value)} />
      </div>

      <div style={styles.filtresWrap}>
        <select style={styles.select} value={filtreType} onChange={e => setFiltreType(e.target.value)}>
          {types.map(t => <option key={t}>{t}</option>)}
        </select>
        <select style={styles.select} value={filtreWilaya} onChange={e => setFiltreWilaya(e.target.value)}>
          {wilayas.map(w => <option key={w}>{w}</option>)}
        </select>
        <select style={styles.select} value={filtreComm} onChange={e => setFiltreComm(e.target.value)}>
          <option value="Tous">Tous</option>
          <option value="Non assigné">Non assigné</option>
          {commerciaux.map(c => <option key={c.id} value={c.id}>{c.prenom}</option>)}
        </select>
      </div>

      {selected.length > 0 && (
        <div style={styles.massBar}>
          <span style={styles.massCount}>{selected.length} sélectionné(s)</span>
          <button style={styles.massBtn} onClick={() => setShowAssign(true)}>👤 Assigner</button>
          <button style={styles.massBtnCancel} onClick={() => setSelected([])}>✕</button>
        </div>
      )}

      <div style={styles.listWrap}>
        <div style={styles.selectAllRow} onClick={selectAll}>
          <div style={{ ...styles.checkbox, background: selected.length === clientsFiltres.length && clientsFiltres.length > 0 ? COULEUR : '#fff' }}>
            {selected.length === clientsFiltres.length && clientsFiltres.length > 0 && <span style={{ color: '#fff', fontSize: 11 }}>✓</span>}
          </div>
          <span style={{ fontSize: 12, color: '#888' }}>Tout sélectionner ({clientsFiltres.length})</span>
        </div>

        {loading && <div style={styles.empty}>Chargement...</div>}
        {!loading && clientsFiltres.length === 0 && (
          <div style={styles.empty}>
            <div style={{ fontSize: 32 }}>👥</div>
            <div style={{ marginTop: 8 }}>Aucun client trouvé</div>
            <button style={styles.addClientEmptyBtn} onClick={() => setShowAddClient(true)}>+ Ajouter un client</button>
          </div>
        )}

        {clientsFiltres.map((c) => (
          <div key={c.id} style={styles.clientCard}>
            <div style={styles.clientRow}>
              <button style={{ background: '#E6F1FB', border: 'none', borderRadius: 6, color: '#185FA5', fontSize: 11, padding: '3px 7px', cursor: 'pointer', flexShrink: 0 }} onClick={() => setFicheClient(c)}>👁</button>
            <div style={{ ...styles.checkbox, background: selected.includes(c.id) ? COULEUR : '#fff' }} onClick={() => toggleSelect(c.id)}>
                {selected.includes(c.id) && <span style={{ color: '#fff', fontSize: 11 }}>✓</span>}
              </div>
              <div style={styles.clientAv}>{c.nom?.[0]?.toUpperCase()}</div>
              <div style={{ flex: 1 }} onClick={() => toggleSelect(c.id)}>
                <div style={styles.clientNom}>{c.nom}</div>
                <div style={styles.clientSub}>{c.type || c.statut} · {c.commune || c.wilaya}</div>
              </div>
              <div style={{ ...styles.potentielBadge, background: c.potentiel === 'Fort' ? '#EAF3DE' : c.potentiel === 'Faible' ? '#FCEBEB' : '#FAEEDA', color: c.potentiel === 'Fort' ? '#3B6D11' : c.potentiel === 'Faible' ? '#A32D2D' : '#854F0B' }}>
                {c.potentiel || 'Moyen'}
              </div>
            </div>

            {c.commercialIds?.length > 0 && (
              <div style={styles.assignedWrap}>
                {c.commercialIds.map(cid => (
                  <div key={cid} style={styles.assignedPill}>
                    <span style={{ fontSize: 11 }}>{getNomComm(cid)}</span>
                    <button style={styles.removeBtn} onClick={() => desassigner(c.id, cid)}>✕</button>
                  </div>
                ))}
              </div>
            )}
            {(!c.commercialIds || c.commercialIds.length === 0) && (
              <div style={styles.nonAssigne}>⚠️ Non assigné</div>
            )}

            <div style={styles.gpsRow}>
              {c.gps ? (
                <>
                  <span style={styles.gpsOk}>📍 GPS enregistré</span>
                  <button style={styles.gpsBtn} onClick={() => { setClientACorreger(c); setGpsCorrection(''); }}>Corriger</button>
                </>
              ) : (
                <button style={styles.gpsBtnWarning} onClick={() => { setClientACorreger(c); setGpsCorrection(''); }}>
                  ⚠️ Ajouter position GPS
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* MODAL ASSIGNATION */}
      {showAssign && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>Assigner {selected.length} client(s) à :</div>
            {commerciaux.map(c => (
              <button key={c.id} style={{ ...styles.commChoice, ...(commCible === c.id ? styles.commChoiceActive : {}) }}
                onClick={() => setCommCible(c.id)}>
                <div style={styles.commAv}>{c.prenom?.[0]}</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 500 }}>{c.prenom} {c.nom}</div>
                  <div style={{ fontSize: 11, color: '#888' }}>{(c.wilayas || []).join(', ')}</div>
                </div>
              </button>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
              <button style={styles.btnCancel} onClick={() => setShowAssign(false)}>{t(lang, 'cancel')}</button>
              <button style={{ ...styles.btnAssign, opacity: commCible ? 1 : 0.4 }} onClick={assigner} disabled={saving || !commCible}>
                {saving ? 'En cours...' : '✓ Confirmer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL CORRECTION GPS */}
      {clientACorreger && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalTitle}>📍 Position GPS</div>
            <div style={{ fontSize: 13, color: '#444', marginBottom: 14 }}>
              Client : <strong>{clientACorreger.nom}</strong>
            </div>
            <div style={styles.gpsInstructions}>
              <div>1. Ouvre <strong>Google Maps</strong></div>
              <div>2. Trouve l'adresse exacte du client</div>
              <div>3. Clic droit sur la position → <strong>"Copier les coordonnées"</strong></div>
              <div>4. Colle ici ↓</div>
            </div>
            <input style={{ ...styles.formInput, marginTop: 10, fontSize: 15 }}
              placeholder="Ex: 36.3650, 6.6147"
              value={gpsCorrection}
              onChange={e => setGpsCorrection(e.target.value)}
              autoFocus />
            <div style={{ fontSize: 11, color: '#aaa', marginTop: 6, marginBottom: 16 }}>
              Format : latitude, longitude (séparés par une virgule)
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => { setClientACorreger(null); setGpsCorrection(''); }}>{t(lang, 'cancel')}</button>
              <button style={{ ...styles.btnAssign, opacity: gpsCorrection.trim() ? 1 : 0.4 }}
                onClick={enregistrerGps} disabled={saving || !gpsCorrection.trim()}>
                {saving ? 'Enregistrement...' : '✅ Enregistrer la position'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL NOUVEAU CLIENT */}
      {showAddClient && (
        <div style={styles.modalOverlay}>
          <div style={{ ...styles.modal, maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={styles.modalTitle}>Nouveau client</div>
            {[
              { label: 'Nom *', key: 'nom', placeholder: 'Nom du client ou entreprise' },
              { label: 'Téléphone', key: 'telephone', placeholder: '0X XX XX XX XX' },
              { label: 'Commune', key: 'commune', placeholder: 'Ex: Constantine' },
              { label: 'Wilaya', key: 'wilaya', placeholder: 'Ex: Constantine' },
              { label: 'Adresse', key: 'adresse', placeholder: 'Adresse complète' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>{f.label}</div>
                <input style={styles.formInput} placeholder={f.placeholder} value={nouveauClient[f.key]}
                  onChange={e => setNouveauClient(n => ({ ...n, [f.key]: e.target.value }))} />
              </div>
            ))}

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Position GPS (optionnel)</div>
              <input style={styles.formInput} placeholder="Ex: 36.3650, 6.6147"
                value={nouveauClient.gpsText}
                onChange={e => setNouveauClient(n => ({ ...n, gpsText: e.target.value }))} />
              <div style={{ fontSize: 11, color: '#aaa', marginTop: 4 }}>
                Clic droit sur Google Maps → Copier les coordonnées
              </div>
            </div>

            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Type</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {['Prospect', 'Client actif', 'Nouveau client'].map(t => (
                  <button key={t} style={{ ...styles.chipBtn, ...(nouveauClient.type === t ? styles.chipBtnActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, type: t }))}>{t}</button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 4 }}>Potentiel</div>
              <div style={{ display: 'flex', gap: 6 }}>
                {['Faible', 'Moyen', 'Fort'].map(p => (
                  <button key={p} style={{ ...styles.chipBtn, ...(nouveauClient.potentiel === p ? styles.chipBtnActive : {}) }}
                    onClick={() => setNouveauClient(n => ({ ...n, potentiel: p }))}>{p}</button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 8 }}>
              <button style={styles.btnCancel} onClick={() => setShowAddClient(false)}>{t(lang, 'cancel')}</button>
              <button style={{ ...styles.btnAssign, opacity: nouveauClient.nom ? 1 : 0.4 }}
                onClick={ajouterClient} disabled={saving || !nouveauClient.nom}>
                {saving ? 'Enregistrement...' : '✅ Ajouter'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  app: { maxWidth: 480, margin: '0 auto', minHeight: '100vh', background: '#f5f5f5', display: 'flex', flexDirection: 'column', fontFamily: 'system-ui, sans-serif' },
  topbar: { background: '#1C2833', padding: '1rem 1.25rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 10 },
  backBtn: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '50%', width: 32, height: 32, color: '#fff', fontSize: 16, cursor: 'pointer', flexShrink: 0 },
  topbarTitle: { fontSize: 15, fontWeight: 600 },
  topbarSub: { fontSize: 11, opacity: 0.7, marginTop: 2 },
  addBtn: { background: '#C0392B', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer', flexShrink: 0 },
  searchWrap: { padding: '8px 12px', background: '#fff', borderBottom: '1px solid #eee' },
  searchInput: { width: '100%', padding: '9px 12px', border: '1px solid #eee', borderRadius: 8, fontSize: 14, background: '#f9f9f9', fontFamily: 'system-ui' },
  filtresWrap: { display: 'flex', gap: 6, padding: '8px 12px', background: '#fff', borderBottom: '1px solid #eee' },
  select: { flex: 1, padding: '7px 6px', border: '1px solid #ddd', borderRadius: 8, background: '#f9f9f9', fontSize: 12, color: '#444' },
  massBar: { background: '#1C2833', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 8 },
  massCount: { flex: 1, color: '#fff', fontSize: 13, fontWeight: 500 },
  massBtn: { background: '#C0392B', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, padding: '6px 12px', cursor: 'pointer' },
  massBtnCancel: { background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, padding: '6px 10px', cursor: 'pointer' },
  listWrap: { flex: 1, overflowY: 'auto', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 6 },
  selectAllRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 4px', cursor: 'pointer' },
  checkbox: { width: 20, height: 20, borderRadius: 4, border: '1.5px solid #ccc', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' },
  empty: { textAlign: 'center', color: '#888', padding: '3rem 1rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  addClientEmptyBtn: { marginTop: 8, padding: '10px 20px', background: '#1C2833', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, cursor: 'pointer' },
  clientCard: { background: '#fff', borderRadius: 10, padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' },
  clientRow: { display: 'flex', alignItems: 'center', gap: 8 },
  clientAv: { width: 34, height: 34, borderRadius: '50%', background: '#E6F1FB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#185FA5', flexShrink: 0 },
  clientNom: { fontSize: 13, fontWeight: 600, color: '#1C2833' },
  clientSub: { fontSize: 11, color: '#888', marginTop: 2 },
  potentielBadge: { fontSize: 10, padding: '2px 8px', borderRadius: 99, fontWeight: 500, flexShrink: 0 },
  assignedWrap: { display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  assignedPill: { display: 'flex', alignItems: 'center', gap: 4, background: '#EAF3DE', borderRadius: 99, padding: '3px 8px 3px 10px' },
  removeBtn: { background: 'none', border: 'none', color: '#888', fontSize: 11, cursor: 'pointer', padding: '0 2px' },
  nonAssigne: { fontSize: 11, color: '#A32D2D', marginTop: 6, paddingTop: 6, borderTop: '1px solid #f5f5f5' },
  gpsRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f5f5f5' },
  gpsOk: { fontSize: 11, color: '#3B6D11' },
  gpsBtn: { padding: '4px 10px', border: '1px solid #ddd', borderRadius: 6, background: '#f9f9f9', color: '#666', fontSize: 11, cursor: 'pointer' },
  gpsBtnWarning: { padding: '4px 10px', border: '1px solid #F5C6C6', borderRadius: 6, background: '#FCEBEB', color: '#A32D2D', fontSize: 11, cursor: 'pointer' },
  gpsInstructions: { background: '#f5f5f5', borderRadius: 8, padding: '10px 12px', fontSize: 12, color: '#555', display: 'flex', flexDirection: 'column', gap: 6, lineHeight: 1.5 },
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'flex-end' },
  modal: { background: '#fff', width: '100%', maxWidth: 480, margin: '0 auto', borderRadius: '20px 20px 0 0', padding: '20px 16px 30px' },
  modalTitle: { fontSize: 15, fontWeight: 600, color: '#1C2833', marginBottom: 14 },
  commChoice: { width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', border: '1.5px solid #eee', borderRadius: 10, background: '#fff', cursor: 'pointer', marginBottom: 8, textAlign: 'left' },
  commChoiceActive: { border: '1.5px solid #1C2833', background: '#f0f4f8' },
  commAv: { width: 32, height: 32, borderRadius: '50%', background: '#FCEBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 600, color: '#C0392B', flexShrink: 0 },
  btnCancel: { flex: 1, padding: 11, border: '1px solid #ddd', borderRadius: 10, background: '#fff', color: '#666', fontSize: 14, cursor: 'pointer' },
  btnAssign: { flex: 2, padding: 11, background: '#1C2833', border: 'none', borderRadius: 10, color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  formInput: { width: '100%', padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: 'system-ui' },
  chipBtn: { padding: '6px 12px', border: '1px solid #ddd', borderRadius: 99, background: '#fff', color: '#666', fontSize: 12, cursor: 'pointer' },
  chipBtnActive: { border: '1px solid #1C2833', background: '#1C2833', color: '#fff', fontWeight: 600 },
};
